import { useMemo, useState } from "react";
import { toast } from "sonner";
import { Printer } from "lucide-react";
import { Layout } from "../../components/layout/Layout";
import { Textarea } from "../../components/ui/textarea";
import { Button } from "../../components/ui/button";
import { OffsetField } from "../../components/OffsetField";
import { EtiquettesTabs } from "../../components/EtiquettesTabs";
import { parseNumeros, renderCasierPreview, printCasierLabels, getOffsetMm, CASIER_LABEL_W_MM, CASIER_H_MM } from "../../lib/casierLabel";

export default function DepotCasiersPage() {
  const [texte, setTexte] = useState("");
  const [busy, setBusy] = useState(false);
  const [offset, setOffset] = useState(getOffsetMm());
  const numeros = useMemo(() => parseNumeros(texte), [texte]);
  const apercus = useMemo(() => numeros.slice(0, 24).map((n) => ({ n, src: renderCasierPreview(n).toDataURL("image/png") })), [numeros, offset]); // eslint-disable-line react-hooks/exhaustive-deps

  const onChange = (e) => setTexte(e.target.value.replace(/[^0-9\s,;\-–]/g, "")); // chiffres uniquement

  const imprimer = async () => {
    setBusy(true);
    try {
      const res = await printCasierLabels(numeros);
      if (!res.ok) toast.error(res.error || "Échec de l'impression");
      else if (window.SogoDesktop?.printImage) toast.success(`${numeros.length} étiquette(s) envoyée(s) à l'imprimante`);
    } finally { setBusy(false); }
  };

  return (
    <Layout title="Dépôt — Étiquettes">
      <EtiquettesTabs />
      <div className="max-w-2xl space-y-4" data-testid="casiers-page">
        <div className="rounded-xl border border-border bg-card p-5 space-y-3">
          <label className="text-sm font-medium">Numéros de casiers <span className="text-muted-foreground font-normal">(chiffres uniquement)</span></label>
          <Textarea rows={3} value={texte} onChange={onChange} placeholder="Ex. : 28895   ou   12 13 14   ou   1-20" inputMode="numeric" data-testid="casiers-input" />
          <p className="text-xs text-muted-foreground">Séparez les numéros par un espace, une virgule ou un retour à la ligne. « 5-12 » crée toutes les étiquettes de 5 à 12. Le QR code est généré automatiquement. Étiquette {CASIER_LABEL_W_MM} × {CASIER_H_MM} mm (page 78,7 × 20 mm).</p>
          <OffsetField onChange={setOffset} />
        </div>

        {numeros.length > 0 && (
          <div className="rounded-xl border border-border bg-card p-5" data-testid="casiers-apercu">
            <div className="flex items-center justify-between mb-3">
              <p className="text-sm font-semibold" data-testid="casiers-count">{numeros.length} étiquette{numeros.length > 1 ? "s" : ""}</p>
              <Button onClick={imprimer} disabled={busy} className="gap-2" data-testid="casiers-print"><Printer size={15} />Imprimer</Button>
            </div>
            <div className="flex flex-wrap gap-3">
              {apercus.map((a) => <img key={a.n} src={a.src} alt={`Casier ${a.n}`} className="w-full max-w-[220px] border border-border rounded shadow-sm bg-white" />)}
            </div>
            {numeros.length > apercus.length && <p className="text-xs text-muted-foreground mt-2">… et {numeros.length - apercus.length} autres (aperçu limité à {apercus.length}).</p>}
          </div>
        )}
      </div>
    </Layout>
  );
}
